import { Alert, Button, Descriptions, Form, Input, InputNumber, Modal, Radio, Select, Spin, Table, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { GenealogyBeforeAfter } from './GenealogyPreviewChart';
import type { GenealogyPreview } from './GenealogyPreviewChart';

export type ShiftUplinePreview = {
  anchor_user_id: number;
  anchor_username: string;
  triggered_from_user_id: number;
  line_user_count: number;
  line_users: Array<{ id: number; username: string; is_sub: boolean }>;
  sub_account_count: number;
  referral_downline_master_count: number;
  binary_downline_count: number;
  current_referrer: { id: number; username: string } | null;
  current_parent: { id: number; username: string; position: string | null } | null;
  new_upline: { id: number; username: string };
  new_placement: {
    placement_root_id: number;
    placement_root_username: string;
    parent_id: number;
    parent_username: string;
    position: 'L' | 'R';
    spillover_depth: number;
  };
  genealogy_before: GenealogyPreview;
  genealogy_after: GenealogyPreview;
  note: string;
  would_apply: boolean;
};

type FormValues = {
  new_upline_username: string;
  placement_user_id?: number;
  position: 'L' | 'R';
  note: string;
};

function PreviewBody({
  preview,
  selectedPosition,
  zoneLoading,
  onSelectZone,
}: {
  preview: ShiftUplinePreview;
  selectedPosition: 'L' | 'R';
  zoneLoading: boolean;
  onSelectZone: (zone: 'L' | 'R') => void;
}) {
  return (
    <div style={{ maxHeight: '78vh', overflow: 'auto' }}>
      <Alert
        type={preview.line_user_count <= 1 ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <>
            整条线共 <strong>{preview.line_user_count}</strong> 个账户随主号迁移：
            主号 1 · 子账户 {preview.sub_account_count} · 推荐下线主号{' '}
            {preview.referral_downline_master_count} · 二叉安置下线 {preview.binary_downline_count}
          </>
        }
        description={
          preview.line_user_count <= 1
            ? '若该主号在 PHP 镜像中应有子账户/下线，请硬刷新页面（Cmd+Shift+R）后重新预览。'
            : '仅迁移主号的 recommend + 二叉安置；网体内 referrer / master 关系不变，子账户与安置下线仍挂在原结构下。'
        }
      />

      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        整条线账户明细（共 {preview.line_user_count}）
      </Typography.Text>
      <Table
        size="small"
        style={{ marginBottom: 16 }}
        pagination={{
          pageSize: 25,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          showTotal: (total) => `共 ${total} 个账户`,
        }}
        scroll={{ y: 280 }}
        rowKey="id"
        dataSource={preview.line_users}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 72 },
          { title: '账号', dataIndex: 'username', ellipsis: true },
          {
            title: '类型',
            width: 120,
            render: (_, r) =>
              r.id === preview.anchor_user_id ? (
                <Tag color="gold">迁移主号</Tag>
              ) : r.is_sub ? (
                <Tag color="purple">子账户</Tag>
              ) : (
                <Tag>推荐/安置</Tag>
              ),
          },
        ]}
      />

      <div style={{ marginBottom: 8 }}>
        <Typography.Text strong style={{ marginRight: 12 }}>
          安置区
        </Typography.Text>
        <Radio.Group
          value={selectedPosition}
          disabled={zoneLoading}
          onChange={(e) => onSelectZone(e.target.value as 'L' | 'R')}
          optionType="button"
          buttonStyle="solid"
          options={[
            { value: 'L', label: '左区（含滑落）' },
            { value: 'R', label: '右区（含滑落）' },
          ]}
        />
        {zoneLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
      </div>

      <GenealogyBeforeAfter
        before={preview.genealogy_before}
        after={preview.genealogy_after}
        afterZonePicker={{
          selected: selectedPosition,
          onSelect: onSelectZone,
          loading: zoneLoading,
        }}
      />

      <Descriptions column={1} size="small" bordered style={{ marginTop: 12 }}>
        <Descriptions.Item label="迁移主号">
          #{preview.anchor_user_id} {preview.anchor_username}
          {preview.triggered_from_user_id !== preview.anchor_user_id && (
            <Tag style={{ marginLeft: 8 }}>由子账户 #{preview.triggered_from_user_id} 触发</Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="新上级">
          #{preview.new_upline.id} {preview.new_upline.username}
        </Descriptions.Item>
        <Descriptions.Item label="新安置">
          从 #{preview.new_placement.placement_root_id} {preview.new_placement.placement_root_username}{' '}
          的{preview.new_placement.position === 'L' ? '左' : '右'}区滑落 → 节点 #
          {preview.new_placement.parent_id} {preview.new_placement.parent_username}（深度{' '}
          {preview.new_placement.spillover_depth}）
        </Descriptions.Item>
        <Descriptions.Item label="备注">{preview.note}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}

export function ShiftUplineForm({
  userId,
  username,
  onDone,
}: {
  userId: number;
  username: string;
  onDone?: () => void;
}) {
  const endpoint = `/api/admin/users/${userId}/shift-upline`;
  const [form] = Form.useForm<FormValues>();
  const [preview, setPreview] = useState<ShiftUplinePreview | null>(null);
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [zoneLoading, setZoneLoading] = useState(false);

  async function fetchPreview(values: FormValues): Promise<ShiftUplinePreview> {
    return api.post<ShiftUplinePreview>(endpoint, { ...values, xian: true });
  }

  async function handleSubmit(values: FormValues) {
    setSubmitting(true);
    try {
      const data = await fetchPreview(values);
      setPreview(data);
      setPendingValues(values);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '预览失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSelectZone(position: 'L' | 'R') {
    if (!pendingValues || pendingValues.position === position) return;
    setZoneLoading(true);
    try {
      const next = { ...pendingValues, position };
      const data = await fetchPreview(next);
      setPreview(data);
      setPendingValues(next);
      form.setFieldValue('position', position);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '刷新预览失败');
    } finally {
      setZoneLoading(false);
    }
  }

  async function handleConfirm() {
    if (!pendingValues) return;
    setConfirmLoading(true);
    try {
      await api.post(endpoint, { ...pendingValues, xian: false });
      message.success('操作已完成');
      setPreview(null);
      setPendingValues(null);
      form.resetFields();
      onDone?.();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '操作失败');
    } finally {
      setConfirmLoading(false);
    }
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ position: 'L' }}>
        <Form.Item
          name="new_upline_username"
          label="新上级用户名/编号"
          rules={[{ required: true, message: '请填写新上级用户名或编号' }]}
        >
          <Input placeholder="用户名或编号（如 uo23456 / 106060）" />
        </Form.Item>
        <Form.Item name="placement_user_id" label="安置起点用户 ID 或编号（可选，默认新上级）">
          <InputNumber style={{ width: '100%' }} placeholder="留空则从新上级节点安置；可填内部 ID 或编号" />
        </Form.Item>
        <Form.Item
          name="position"
          label="安置区"
          rules={[{ required: true, message: '请选择安置区' }]}
        >
          <Select
            options={[
              { value: 'L', label: '左区（含滑落）' },
              { value: 'R', label: '右区（含滑落）' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="note"
          label="操作备注"
          rules={[{ required: true, message: '请填写操作备注' }]}
        >
          <Input.TextArea rows={3} placeholder={`平移 ${username} 整条线的原因…`} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            预览迁移影响
          </Button>
        </Form.Item>
      </Form>

      <Modal
        open={preview !== null}
        title="确认执行"
        width={1080}
        onOk={handleConfirm}
        onCancel={() => {
          setPreview(null);
          setPendingValues(null);
        }}
        confirmLoading={confirmLoading}
        okButtonProps={{ disabled: zoneLoading }}
        okText="确认执行"
        cancelText="取消"
      >
        {preview && pendingValues && (
          <PreviewBody
            preview={preview}
            selectedPosition={pendingValues.position}
            zoneLoading={zoneLoading}
            onSelectZone={handleSelectZone}
          />
        )}
      </Modal>
    </>
  );
}
